import { useCallback, useEffect, useRef, useState } from 'react';
import { getInviteMessages, getInviteParticipants, getHangoutInvite, getMyHangoutInvites, getPublicHangoutInvite, toApiError } from './api';
import { ApiError, HangoutInvite, HangoutMessage, HangoutParticipant, PublicHangoutInvite } from './types';

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (value: T | null) => void;
}

// Small data hook in the style of hooks/useVenues.ts: fetch on mount / when the key changes,
// expose reload, and ignore responses that arrive after the component moved on.
export function useAsync<T>(fetcher: (() => Promise<T>) | null, key: unknown): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(fetcher !== null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const generation = useRef(0);

  const reload = useCallback(async () => {
    const fn = fetcherRef.current;
    if (!fn) return;
    const mine = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const value = await fn();
      if (mine === generation.current) setData(value);
    } catch (err) {
      if (mine === generation.current) setError(toApiError(err));
    } finally {
      if (mine === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data, error, loading, reload, setData };
}

export const useHangoutInvite = (id: number | null) =>
  useAsync<HangoutInvite>(id ? () => getHangoutInvite(id) : null, id);

export const usePublicHangoutInvite = (token: string | undefined, ref: string | null, authKey: string) =>
  useAsync<PublicHangoutInvite>(token ? () => getPublicHangoutInvite(token, ref) : null, `${token}|${ref}|${authKey}`);

export const useMyHangoutInvites = () => useAsync<HangoutInvite[]>(getMyHangoutInvites, 'mine');

export const useInviteParticipants = (id: number | null) =>
  useAsync<HangoutParticipant[]>(id ? () => getInviteParticipants(id) : null, id);

// Group chat: loads the latest page, then polls for newer messages every few seconds while the
// tab is visible. The polling lives here so a SignalR push can replace it without touching pages.
export function useInviteMessages(inviteId: number | null, enabled: boolean, pollMs = 5000) {
  const [messages, setMessages] = useState<HangoutMessage[]>([]);
  const [nextCursor, setNextCursor] = useState<number | undefined>();
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const newest = useRef<number | undefined>(undefined);

  const merge = useCallback((incoming: HangoutMessage[], prepend = false) => {
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => !seen.has(m.id));
      const next = prepend ? [...fresh, ...prev] : [...prev, ...fresh];
      if (next.length) newest.current = Math.max(newest.current ?? 0, ...next.map((m) => m.id));
      return next;
    });
  }, []);

  useEffect(() => {
    if (!inviteId || !enabled) return;
    let cancelled = false;
    setLoading(true);
    getInviteMessages(inviteId, { limit: 30 })
      .then((page) => { if (!cancelled) { merge(page.items); setNextCursor(page.nextCursor); } })
      .catch((err) => { if (!cancelled) setError(toApiError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });

    const timer = window.setInterval(() => {
      if (document.hidden) return;
      getInviteMessages(inviteId, { after: newest.current ?? 0 })
        .then((page) => merge(page.items))
        .catch(() => undefined);
    }, pollMs);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [inviteId, enabled, pollMs, merge]);

  const loadOlder = useCallback(async () => {
    if (!inviteId || !nextCursor) return;
    const page = await getInviteMessages(inviteId, { cursor: nextCursor, limit: 30 });
    merge(page.items, true);
    setNextCursor(page.nextCursor);
  }, [inviteId, nextCursor, merge]);

  return { messages, loading, error, hasOlder: !!nextCursor, loadOlder, addMessage: (m: HangoutMessage) => merge([m]) };
}
